import { defineStore } from 'pinia';
import {
  getOAuthToken,
  loginWithRedirect,
  logout,
  LogoutOptions,
  OIDCTokenModel,
  OAuthTokenOptions,
  RedirectCallbackHandlerOptions,
  RedirectLoginOptions,
  Transaction,
  TenantModel,
  TenantType,
} from '~app/core/auth';
import {
  getTransactionById,
  patchTransactionById,
  removeTransaction,
} from '~app/core/auth/client/transaction';
import { AppError } from '~app/core/error/app-error';
import { storage } from '~app/shared/storage';
import { accountService } from '~app/modules/account/service';
import { AccountModel } from '~app/modules/account/types';
import {
  CurrentUserProductModel,
  CurrentUserProductsModel,
  StateAuthModel,
} from '~app/core/auth/store/types';
import { useConfigStore } from '~app/core/config';

export const STORE_ID = 'auth';

export let oAuthTokenRequestPromise: Promise<OIDCTokenModel>;

//TODO move to a separate file / fix typing issue once https://github.com/vuejs/pinia/issues/802 fixed
export const useAuthStore = defineStore(STORE_ID, {
  state: (): Pick<StateAuthModel, 'user' | 'token'> => ({
    user: void 0,
    token: void 0,
  }),
  getters: {
    currentUserId: (state: StateAuthModel): string => state.token?.accessTokenDecoded?.sub,
    currentUserTenant: (state: StateAuthModel): TenantModel => {
      const organizationSlug = state.token?.accessTokenDecoded?.organizationSlug;
      return state.user?.tenants.filter(
        (tenant: TenantModel) => tenant.slug === organizationSlug,
      )[0];
    },
    currentUserProducers: (state: StateAuthModel): TenantModel[] => {
      return (
        state.user?.tenants.filter((tenant: TenantModel) => tenant.type === TenantType.PRODUCER) ||
        []
      );
    },
    currentUserProducts: (state: StateAuthModel): CurrentUserProductsModel => {
      const { products } = useConfigStore();

      function resolveSlugByProductName(productName: string): string | void {
        if (
          state.currentUserTenant?.type === TenantType.PRODUCER &&
          state.currentUserTenant?.products.includes(productName)
        ) {
          return state.currentUserTenant.slug;
        }

        const productUserTenants = state.currentUserProducers?.filter((producer) =>
          producer.products.includes(productName),
        );

        if (productUserTenants.length === 1) {
          return productUserTenants[0].slug;
        }
      }

      return Object.keys(products).reduce((acc, it) => {
        const { avatar, name, appUrl, aboutUrl } = products[it];

        const slug = resolveSlugByProductName(it);

        const url = slug
          ? `${appUrl.replace('%SLUG%', slug)}?sub=${state.currentUserId}`
          : aboutUrl;

        acc[it] = { avatar, name, url } as CurrentUserProductModel;
        return acc;
      }, {} as CurrentUserProductsModel);
    },
    isAuthenticated: (state: StateAuthModel): boolean => !!state.token,
    isAccessTokenExpired: (state: StateAuthModel): boolean =>
      new Date() > new Date(state?.token?.expiresAt),
  },
  actions: {
    clearStorage() {
      storage.remove(STORE_ID);
      removeTransaction();
    },
    fetchOAuthToken(options: OAuthTokenOptions): Promise<OIDCTokenModel> {
      if (!oAuthTokenRequestPromise) {
        oAuthTokenRequestPromise = getOAuthToken(options);
      }

      return oAuthTokenRequestPromise.then((token) => {
        oAuthTokenRequestPromise = null;
        this.$patch({ token });
        return this.$state.token;
      });
    },
    fetchUser(): Promise<AccountModel> {
      const id = this.$state.token?.accessTokenDecoded?.sub;

      if (!id) {
        return Promise.reject();
      }

      return accountService.getById(id).then((user) => {
        this.$patch({ user });
        return this.$state.user;
      });
    },
    handleRedirectCallback({
      code,
      error,
      errorDescription,
      state: transactionId,
    }: RedirectCallbackHandlerOptions): Promise<string> {
      const transaction = <Transaction>getTransactionById(transactionId);

      if (error || errorDescription) {
        return Promise.reject(new AppError(error, errorDescription, '400'));
      }

      if (!code || !transaction) {
        return Promise.reject(new AppError('Forbidden', null, '403'));
      }

      const { codeVerifier, targetUrl, token } = transaction;

      return Promise.resolve<OIDCTokenModel>(
        token || this.fetchOAuthToken({ code, codeVerifier, grantType: 'authorization_code' }),
      )
        .then((token) => patchTransactionById({ token }, transactionId))
        .then(() => this.fetchUser())
        .then(() => removeTransaction())
        .then(() => targetUrl);
    },
    loginWithRedirect(options: RedirectLoginOptions = {}): Promise<void> {
      this.clearStorage();
      return loginWithRedirect(options);
    },
    logout(options?: LogoutOptions): Promise<void> {
      this.clearStorage();
      return logout(options);
    },
    refreshOAuthToken(): Promise<OIDCTokenModel> {
      const { refreshToken } = this.token;
      return this.fetchOAuthToken({
        refreshToken,
        grantType: 'refresh_token',
      });
    },
  },
});
